import { sanitizeUploadFileName } from 'utils/sanitizeUploadFileName';

const VIDEO_EXTENSIONS = ['webp', 'wmv', 'mpg', 'webm', 'mov', 'mpeg', 'mp4', 'm4v', 'avi', 'mkv'];

/**
 * Extracts the file name from an uploads path.
 * @param {string} uploadPath
 * @returns {string}
 */
export function getUploadFileName(uploadPath) {
    if (!uploadPath || typeof uploadPath !== 'string') {
        return '';
    }
    return uploadPath.split('/').pop();
}

/**
 * @param {string} uploadPath
 * @returns {boolean}
 */
export function isUploadVideo(uploadPath) {
    const fileName = getUploadFileName(uploadPath).toLowerCase();
    const extension = fileName.split('.').pop();
    return VIDEO_EXTENSIONS.includes(extension);
}

/**
 * @param {string} uploadPath
 * @returns {boolean}
 */
export function isValidUploadPath(uploadPath) {
    if (!uploadPath || typeof uploadPath !== 'string') {
        return false;
    }
    if (uploadPath === 'undefined' || uploadPath.includes('undefined')) {
        return false;
    }
    if (uploadPath === 'Non') {
        return false;
    }
    return Boolean(getUploadFileName(uploadPath));
}

/**
 * Builds a stream URL for an uploaded file.
 * @param {string} baseUrl
 * @param {string} uploadPath
 * @param {boolean} isVideo
 * @returns {string}
 */
export function buildUploadUrl(baseUrl, uploadPath, isVideo = false) {
    if (!baseUrl || baseUrl === 'undefined' || !isValidUploadPath(uploadPath)) {
        return '';
    }
    const rawFileName = getUploadFileName(uploadPath);
    if (!rawFileName) {
        return '';
    }
    const fileName = sanitizeUploadFileName(rawFileName);
    const encodedFileName = encodeURIComponent(fileName);
    const streamType = isVideo ? 'video/' : '';
    return `${baseUrl}/api/v1/uploads/${streamType}${encodedFileName}`;
}

/**
 * Builds preview URL for dashboard media components.
 * @param {string} baseUrl
 * @param {string} uploadPath
 * @returns {string}
 */
export function buildMediaPreviewUrl(baseUrl, uploadPath) {
    if (!isValidUploadPath(uploadPath) || !baseUrl || baseUrl === 'undefined') {
        return '';
    }
    return buildUploadUrl(baseUrl, uploadPath, isUploadVideo(uploadPath));
}

/**
 * Converts legacy `/uploads/...` URLs to the streaming API path.
 * @param {string} mediaUrl
 * @returns {string}
 */
export function normalizeLegacyUploadUrl(mediaUrl) {
    if (!mediaUrl || typeof mediaUrl !== 'string') {
        return '';
    }
    if (mediaUrl === 'undefined' || /\/undefined(?:\/|$)/.test(mediaUrl)) {
        return '';
    }
    if (
        mediaUrl.includes('/api/v1/uploads/')
        || mediaUrl.startsWith('blob:')
        || mediaUrl.startsWith('data:')
    ) {
        return mediaUrl;
    }
    const legacyMatch = mediaUrl.match(/^(https?:\/\/[^/]+)\/(uploads\/.+)$/);
    if (legacyMatch) {
        const [, baseUrl, uploadPath] = legacyMatch;
        return buildMediaPreviewUrl(baseUrl, uploadPath);
    }
    return mediaUrl;
}

export { sanitizeUploadFileName } from 'utils/sanitizeUploadFileName';

export default buildUploadUrl;
